<template>
	<div class="container py-4">
		<div class="d-flex justify-content-between align-items-center mb-4">
			<div class="d-flex align-items-center">
				<RouterLink
					class="btn btn-outline-secondary me-3"
					to="/_/configuration/integration/vectorsurv"
					title="Back to VectorSurv"
				>
					<i class="bi bi-arrow-left"></i>
				</RouterLink>
				<div>
					<h1 class="mb-0">VectorSurv synchronization</h1>
					<p class="text-muted mb-0">
						What has been filed, what has been imported, and what could not be.
					</p>
				</div>
			</div>
		</div>

		<div v-if="error" class="alert alert-danger" role="alert">{{ error }}</div>

		<!-- Status: the two switches and both directions' latest runs. -->
		<div class="card mb-4 sync-card">
			<div
				class="card-header d-flex justify-content-between align-items-center"
			>
				<h2 class="h5 mb-0">Status</h2>
				<span v-if="config?.filingEnabled" class="badge text-bg-success"
					>filing</span
				>
				<span v-else class="badge text-bg-secondary">paused</span>
			</div>
			<div class="card-body">
				<p class="mb-1">
					Connection:
					<span v-if="config?.tokenConfigured" class="text-success"
						>configured</span
					>
					<span v-else class="text-danger">no token stored</span>
					<RouterLink
						class="ms-2 small"
						to="/_/configuration/integration/vectorsurv/connection"
					>
						connection
					</RouterLink>
				</p>
				<p class="mb-1">
					Filing started:
					<template v-if="config?.filingStartedAt">
						<TimeRelativeWithTooltip :time="config.filingStartedAt" />
						<span class="text-muted small ms-1">
							nothing older than this is filed
						</span>
					</template>
					<span v-else class="text-muted">not started</span>
				</p>
				<p class="mb-3">
					Last outbound run:
					<template v-if="latestOutbound">
						{{ latestOutbound.status }}
						<TimeRelativeWithTooltip
							:time="latestOutbound.completedAt || latestOutbound.created"
						/>
						<span class="text-muted small">
							(filed {{ latestOutbound.filed }}, updated
							{{ latestOutbound.updated }}, unchanged
							{{ latestOutbound.unchanged }}, blocked
							{{ latestOutbound.blocked }}, failed {{ latestOutbound.failed }})
						</span>
					</template>
					<span v-else class="text-muted">never</span>
				</p>
				<p class="mb-3">
					Last inbound run:
					<template v-if="latestInbound">
						{{ latestInbound.status }}
						<TimeRelativeWithTooltip
							:time="latestInbound.completedAt || latestInbound.created"
						/>
						<span v-if="latestInbound.message" class="text-muted small">
							{{ latestInbound.message }}
						</span>
					</template>
					<span v-else class="text-muted">never</span>
				</p>

				<div class="d-flex gap-2 flex-wrap">
					<ButtonLoading
						v-if="!config?.filingEnabled"
						text="Start filing"
						icon="bi bi-play-fill"
						:loading="starting"
						@click="startFiling"
					/>
					<ButtonLoading
						v-else
						text="Pause"
						icon="bi bi-pause-fill"
						:loading="pausing"
						@click="pauseFiling"
					/>
					<ButtonLoading
						text="Run now"
						icon="bi bi-arrow-repeat"
						:loading="filingNow"
						@click="runNow"
					/>
					<ButtonLoading
						text="Full mirror"
						icon="bi bi-cloud-arrow-down"
						:loading="mirroring"
						@click="fullMirror"
					/>
				</div>

				<div
					v-if="actionError"
					class="alert alert-danger mt-3 mb-0"
					role="alert"
				>
					{{ actionError }}
				</div>
				<div
					v-if="actionResult"
					class="alert alert-success mt-3 mb-0"
					role="status"
				>
					{{ actionResult }}
				</div>
			</div>
		</div>

		<!-- Readiness: what starting would do, before anything has run. -->
		<div v-if="readiness" class="card mb-4 sync-card">
			<div class="card-header"><h2 class="h5 mb-0">Readiness</h2></div>
			<div class="card-body">
				<div
					v-if="!readiness.equipment"
					class="alert alert-warning"
					role="alert"
				>
					Add at least one piece of equipment before starting to file: with
					none, no treatment can be annotated and nothing can file.
					<RouterLink to="/_/configuration/integration/vectorsurv/equipment">
						Add equipment
					</RouterLink>
				</div>
				<p class="mb-1">
					<strong>{{ readiness.candidates.toLocaleString() }}</strong>
					treatment(s) filing would consider
					<template v-if="readiness.examined !== readiness.candidates">
						—
						<strong>{{ readiness.examined.toLocaleString() }}</strong> examined
						for this report
					</template>
				</p>
				<p class="mb-2">
					<strong>{{ readiness.ready.toLocaleString() }}</strong> would file
					now.
				</p>
				<div v-if="readiness.blockers.length" class="table-responsive">
					<table class="table table-sm align-middle mb-0">
						<thead>
							<tr>
								<th scope="col">Blocking reason</th>
								<th scope="col" class="text-end">Treatments</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="blocker in readiness.blockers" :key="blocker.reason">
								<td>{{ blocker.reason }}</td>
								<td class="text-end">{{ blocker.count.toLocaleString() }}</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>

		<!-- Outbound runs. -->
		<div class="card mb-4 sync-card">
			<div class="card-header"><h2 class="h5 mb-0">Outbound runs</h2></div>
			<div class="card-body">
				<p v-if="!outboundRuns.length" class="text-muted mb-0">
					No filing pass has run yet.
				</p>
				<div v-else class="table-responsive">
					<table class="table table-sm align-middle mb-0">
						<thead>
							<tr>
								<th scope="col">Created</th>
								<th scope="col">Trigger</th>
								<th scope="col">Status</th>
								<th scope="col" class="text-end">Examined</th>
								<th scope="col" class="text-end">Filed</th>
								<th scope="col" class="text-end">Updated</th>
								<th scope="col" class="text-end">Unchanged</th>
								<th scope="col" class="text-end">Blocked</th>
								<th scope="col" class="text-end">Failed</th>
								<th scope="col">Events</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="run in outboundRuns" :key="run.id">
								<td><TimeRelativeWithTooltip :time="run.created" /></td>
								<td>{{ run.trigger }}</td>
								<td>
									<span :class="statusBadge(run.status)">{{ run.status }}</span>
									<span v-if="run.error" class="text-danger small ms-1">
										{{ run.error }}
									</span>
								</td>
								<td class="text-end">{{ run.examined }}</td>
								<td class="text-end">{{ run.filed }}</td>
								<td class="text-end">{{ run.updated }}</td>
								<td class="text-end">{{ run.unchanged }}</td>
								<td class="text-end">{{ run.blocked }}</td>
								<td class="text-end">{{ run.failed }}</td>
								<td>
									<a :href="runEventsCsv(run.id)">CSV</a>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>

		<!-- Blocked, grouped by reason: the report's actionable half. -->
		<div class="card mb-4 sync-card">
			<div class="card-header"><h2 class="h5 mb-0">Blocked</h2></div>
			<div class="card-body">
				<p v-if="!blockedGroups.length" class="text-muted mb-0">
					Nothing is blocked.
				</p>
				<div v-else class="table-responsive">
					<table class="table table-sm align-middle mb-0">
						<thead>
							<tr>
								<th scope="col">Reason</th>
								<th scope="col" class="text-end">Records</th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="group in blockedGroups"
								:key="group.reason"
								class="blocked-group"
								@click="filterState('blocked', group.reason)"
							>
								<td>
									<span class="badge text-bg-warning me-2">{{
										group.reason
									}}</span>
									<span class="text-muted small">{{ group.detail }}</span>
								</td>
								<td class="text-end">{{ group.count }}</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>

		<!-- Diverged: filed, then withdrawn locally or changed upstream. -->
		<div class="card mb-4 sync-card">
			<div class="card-header"><h2 class="h5 mb-0">Diverged</h2></div>
			<div class="card-body">
				<p v-if="!diverged.length" class="text-muted mb-0">
					Nothing filed has diverged: no record was withdrawn in Nidus, and no
					filed application was edited in VectorSurv.
				</p>
				<div v-else class="table-responsive">
					<table class="table table-sm align-middle mb-0">
						<thead>
							<tr>
								<th scope="col">Nidus record</th>
								<th scope="col">VectorSurv id</th>
								<th scope="col">Why</th>
								<th scope="col">Updated</th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="row in diverged"
								:key="row.id"
								class="clickable-row"
								@click="openRecord(row.id)"
							>
								<td>{{ recordLabel(row) }}</td>
								<td>{{ row.remoteId ?? "—" }}</td>
								<td>
									<span
										v-if="row.state === 'diverged'"
										class="badge text-bg-secondary"
									>
										withdrawn in Nidus, still filed
									</span>
									<span v-if="row.upstreamChanged" class="badge text-bg-info">
										changed in VectorSurv
									</span>
								</td>
								<td><TimeRelativeWithTooltip :time="row.updated" /></td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>

		<!-- The ledger. -->
		<div class="card mb-4 sync-card">
			<div
				class="card-header d-flex justify-content-between align-items-center"
			>
				<h2 class="h5 mb-0">Ledger</h2>
				<a :href="recordsCsv">CSV</a>
			</div>
			<div class="card-body">
				<div class="d-flex gap-2 flex-wrap mb-3">
					<select
						v-model="filters.resource"
						class="form-select form-select-sm"
						style="width: auto"
						@change="loadRecords"
					>
						<option value="">Every resource</option>
						<option value="application">application</option>
						<option value="application_source">application_source</option>
						<option value="site">site</option>
						<option value="application_equipment">application_equipment</option>
						<option value="application_technician">
							application_technician
						</option>
					</select>
					<select
						v-model="filters.state"
						class="form-select form-select-sm"
						style="width: auto"
						@change="loadRecords"
					>
						<option value="">Every state</option>
						<option value="pending">pending</option>
						<option value="blocked">blocked</option>
						<option value="synced">synced</option>
						<option value="diverged">diverged</option>
					</select>
					<label class="form-check form-check-inline align-self-center mb-0">
						<input
							v-model="filters.needsUpdate"
							class="form-check-input"
							type="checkbox"
							@change="loadRecords"
						/>
						<span class="form-check-label small">needs update</span>
					</label>
					<label class="form-check form-check-inline align-self-center mb-0">
						<input
							v-model="filters.upstreamChanged"
							class="form-check-input"
							type="checkbox"
							@change="loadRecords"
						/>
						<span class="form-check-label small">upstream changed</span>
					</label>
				</div>

				<p v-if="!records.length" class="text-muted mb-0">
					No record has crossed the boundary yet.
				</p>
				<div v-else class="table-responsive ledger-table">
					<table class="table table-sm align-middle mb-0">
						<thead>
							<tr>
								<th scope="col">Nidus record</th>
								<th scope="col">VectorSurv id</th>
								<th scope="col">State</th>
								<th scope="col">Filed</th>
								<th scope="col">Flags</th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="row in records"
								:key="row.id"
								class="clickable-row"
								@click="openRecord(row.id)"
							>
								<td>
									<RouterLink
										v-if="row.treatmentId"
										:to="`/_/review/treatment/${row.treatmentId}`"
										@click.stop
									>
										treatment {{ row.treatmentId }}
									</RouterLink>
									<span v-else>{{ recordLabel(row) }}</span>
									<span class="text-muted small ms-2">{{ row.resource }}</span>
								</td>
								<td>{{ row.remoteId ?? "—" }}</td>
								<td>
									<span :class="stateBadge(row.state)">{{ row.state }}</span>
									<span v-if="row.blockedReason" class="text-muted small ms-1">
										{{ row.blockedReason }}
									</span>
								</td>
								<td>
									<TimeRelativeWithTooltip
										v-if="row.filedAt"
										:time="row.filedAt"
									/>
									<span v-else class="text-muted">—</span>
								</td>
								<td>
									<span
										v-if="row.needsUpdate"
										class="badge text-bg-warning me-1"
									>
										needs update
									</span>
									<span v-if="row.upstreamChanged" class="badge text-bg-info">
										upstream changed
									</span>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>

		<!-- Record detail: the attempts, with the bodies they carried. -->
		<div v-if="detail" class="card mb-4 sync-card">
			<div
				class="card-header d-flex justify-content-between align-items-center"
			>
				<h2 class="h5 mb-0">
					Record {{ detail.id }}: {{ recordLabel(detail) }}
				</h2>
				<button
					type="button"
					class="btn btn-sm btn-outline-secondary"
					@click="closeRecord"
				>
					Close
				</button>
			</div>
			<div class="card-body">
				<p class="mb-1">
					<span :class="stateBadge(detail.state)">{{ detail.state }}</span>
					<span class="text-muted small ms-2">{{ detail.resource }}</span>
					<span class="text-muted small ms-2">local {{ detail.localKey }}</span>
					<span class="text-muted small ms-2">
						VectorSurv id {{ detail.remoteId ?? "—" }}
					</span>
				</p>
				<p v-if="detail.contentHash" class="text-muted small mb-1">
					last filed body {{ detail.contentHash }}
				</p>
				<p v-if="detail.lastError" class="text-danger small mb-2">
					last error: {{ detail.lastError }}
				</p>

				<p v-if="!events.length" class="text-muted mb-0">
					This record has no attempts yet.
				</p>
				<div
					v-for="(event, index) in events"
					:key="event.id"
					class="event-row mb-3"
				>
					<p class="mb-1">
						<strong>{{ event.action }}</strong>
						<span :class="outcomeBadge(event.outcome)">{{
							event.outcome
						}}</span>
						<TimeRelativeWithTooltip
							:time="event.at"
							class="ms-2 small text-muted"
						/>
						<span v-if="event.error" class="text-danger small ms-2">
							{{ event.error }}
						</span>
					</p>
					<div class="row g-2">
						<div class="col-md-6">
							<div class="small text-muted">request</div>
							<pre class="payload">{{ pretty(event.request) }}</pre>
						</div>
						<div class="col-md-6">
							<div class="small text-muted">response</div>
							<pre class="payload">{{ pretty(event.response) }}</pre>
						</div>
					</div>
					<div v-if="previousRequest(index)" class="mt-2">
						<div class="small text-muted">
							diff against the previous request body
						</div>
						<pre
							class="payload"
						> <span v-for="(line, i) in requestDiff(index)" :key="i" :class="line.changed ? 'diff-changed' : ''">{{ line.text }}
</span></pre>
					</div>
				</div>
			</div>
		</div>

		<!-- The inbound mirror, moved off the settings page. -->
		<div class="card mb-4 sync-card">
			<div
				class="card-header d-flex justify-content-between align-items-center"
			>
				<h2 class="h5 mb-0">Inbound mirror</h2>
				<span v-if="mirror?.running" class="badge text-bg-info"
					>running now</span
				>
			</div>
			<div class="card-body">
				<p v-if="mirrorError" class="text-danger mb-0">{{ mirrorError }}</p>
				<template v-else-if="mirror && mirror.syncedAt">
					<p class="mb-1">
						Last synced <TimeRelativeWithTooltip :time="mirror.syncedAt" />
					</p>
					<p class="text-muted mb-2">
						{{ mirror.walked }} of {{ mirror.total }} resources
						mirrored<template v-if="mirror.skipped"
							>, {{ mirror.skipped }} skipped</template
						>.
					</p>
					<div
						v-if="mirror.skippedResources.length"
						class="table-responsive mb-3"
					>
						<table class="table table-sm align-middle mb-0">
							<thead>
								<tr>
									<th scope="col">Not mirrored</th>
									<th scope="col">Why</th>
								</tr>
							</thead>
							<tbody>
								<tr
									v-for="skip in mirror.skippedResources"
									:key="skip.resourceType"
								>
									<td>{{ skip.resourceType }}</td>
									<td>
										<span class="badge text-bg-secondary me-2">{{
											skip.cause
										}}</span>
										<span class="text-muted small">{{ skip.detail }}</span>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
					<div
						v-if="mirror.resourceCounts.length"
						class="table-responsive mirror-counts"
					>
						<table class="table table-sm align-middle mb-0">
							<thead>
								<tr>
									<th scope="col">Resource</th>
									<th scope="col" class="text-end">Rows</th>
								</tr>
							</thead>
							<tbody>
								<tr
									v-for="count in mirror.resourceCounts"
									:key="count.resourceType"
								>
									<td>{{ count.resourceType }}</td>
									<td class="text-end">{{ count.rows.toLocaleString() }}</td>
								</tr>
							</tbody>
						</table>
					</div>
				</template>
				<p v-else class="text-muted mb-0">
					The mirror has not run yet; the daily schedule keeps it current once
					it has.
				</p>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";
import { apiClient } from "@/client";
import ButtonLoading from "@/components/common/ButtonLoading.vue";
import TimeRelativeWithTooltip from "@/components/TimeRelativeWithTooltip.vue";
import type {
	VectorSurvConfig,
	VectorSurvFilingReadiness,
	VectorSurvSyncEvent,
	VectorSurvSyncObject,
	VectorSurvSyncQueued,
	VectorSurvSyncRun,
	VectorSurvSyncStatus,
} from "@/type/vectorsurv";

// The outbound half of the VectorSurv integration, in one place: the two
// switches, what a filing pass would do before it has run, what every pass did,
// and the ledger with each attempt's payload behind it. The mirror report lives
// here too — both directions in one page is the point of the run table.

const error = ref("");
const config = ref<VectorSurvConfig | null>(null);
const readiness = ref<VectorSurvFilingReadiness | null>(null);
const outboundRuns = ref<VectorSurvSyncRun[]>([]);
const latestOutbound = ref<VectorSurvSyncRun | null>(null);
const latestInbound = ref<VectorSurvSyncRun | null>(null);
const records = ref<VectorSurvSyncObject[]>([]);
const detail = ref<VectorSurvSyncObject | null>(null);
const events = ref<VectorSurvSyncEvent[]>([]);
const mirror = ref<VectorSurvSyncStatus | null>(null);
const mirrorError = ref("");

const starting = ref(false);
const pausing = ref(false);
const filingNow = ref(false);
const mirroring = ref(false);
const actionError = ref("");
const actionResult = ref("");

const filters = reactive({
	resource: "",
	state: "",
	needsUpdate: false,
	upstreamChanged: false,
});

// fragment asks the server for the ledger rows the filter names. A flag that is
// off is omitted rather than sent as false, so "any" and "false" stay distinct.
function ledgerQuery(): string {
	const params = new URLSearchParams();
	if (filters.resource) params.set("resource", filters.resource);
	if (filters.state) params.set("state", filters.state);
	if (filters.needsUpdate) params.set("needs_update", "true");
	if (filters.upstreamChanged) params.set("upstream_changed", "true");
	params.set("limit", "200");
	return params.toString();
}

// blockedGroups tallies the blocked rows the ledger read returned by reason, so
// the district sees one row per thing it has to fix rather than one per record.
const blockedGroups = computed(() => {
	const tally = new Map<string, number>();
	for (const row of records.value) {
		if (row.state !== "blocked") continue;
		const reason = row.blockedReason || "unspecified";
		tally.set(reason, (tally.get(reason) ?? 0) + 1);
	}
	return [...tally.entries()]
		.map(([reason, count]) => ({ reason, count, detail: "" }))
		.sort((a, b) => b.count - a.count || a.reason.localeCompare(b.reason));
});

// diverged is the "filed, then changed" list: withdrawn locally keeps its record
// upstream, and an upstream edit is reported rather than overwritten.
const diverged = computed(() =>
	records.value.filter(
		(row) => row.state === "diverged" || row.upstreamChanged,
	),
);

const recordsCsv = "/api/integration/vectorsurv/export/records.csv";

function runEventsCsv(runId: number): string {
	return `/api/integration/vectorsurv/export/runs/${runId}.csv`;
}

function recordLabel(row: VectorSurvSyncObject): string {
	if (row.treatmentId) return `treatment ${row.treatmentId}`;
	if (row.featureId) return `feature ${row.featureId}`;
	if (row.trapStationId) return `trap station ${row.trapStationId}`;
	if (row.equipmentId) return `equipment ${row.equipmentId}`;
	if (row.userId) return `user ${row.userId}`;
	return row.localKey;
}

function stateBadge(state: string): string {
	switch (state) {
		case "synced":
			return "badge text-bg-success";
		case "blocked":
			return "badge text-bg-warning";
		case "diverged":
			return "badge text-bg-secondary";
		default:
			return "badge text-bg-light text-dark";
	}
}

function statusBadge(status: string): string {
	switch (status) {
		case "succeeded":
			return "badge text-bg-success";
		case "failed":
			return "badge text-bg-danger";
		case "running":
			return "badge text-bg-info";
		default:
			return "badge text-bg-light text-dark";
	}
}

function outcomeBadge(outcome: string): string {
	switch (outcome) {
		case "filed":
			return "badge text-bg-success ms-1";
		case "blocked":
			return "badge text-bg-warning ms-1";
		case "failed":
			return "badge text-bg-danger ms-1";
		default:
			return "badge text-bg-light text-dark ms-1";
	}
}

function errorMessage(e: any, fallback: string): string {
	return e?.response?.data?.message || e?.message || fallback;
}

async function loadConfig(): Promise<void> {
	config.value = await apiClient.JSONGet<VectorSurvConfig>(
		"/api/integration/vectorsurv",
	);
}

async function loadRuns(): Promise<void> {
	const [outbound, latest, inbound] = await Promise.all([
		apiClient.JSONGet<VectorSurvSyncRun[]>(
			"/api/integration/vectorsurv/runs?direction=outbound&limit=20",
		),
		apiClient.JSONGet<VectorSurvSyncRun | null>(
			"/api/integration/vectorsurv/runs/latest?direction=outbound",
		),
		apiClient.JSONGet<VectorSurvSyncRun | null>(
			"/api/integration/vectorsurv/runs/latest?direction=inbound",
		),
	]);
	outboundRuns.value = outbound ?? [];
	latestOutbound.value = latest;
	latestInbound.value = inbound;
}

async function loadReadiness(): Promise<void> {
	readiness.value = await apiClient.JSONGet<VectorSurvFilingReadiness>(
		"/api/integration/vectorsurv/filing/readiness",
	);
}

async function loadRecords(): Promise<void> {
	records.value = await apiClient.JSONGet<VectorSurvSyncObject[]>(
		`/api/integration/vectorsurv/records?${ledgerQuery()}`,
	);
}

async function loadMirror(): Promise<void> {
	mirrorError.value = "";
	try {
		mirror.value = await apiClient.JSONGet<VectorSurvSyncStatus>(
			"/api/integration/vectorsurv/sync",
		);
	} catch (e: any) {
		mirrorError.value = errorMessage(e, "Failed to read the mirror.");
	}
}

async function load(): Promise<void> {
	error.value = "";
	try {
		await loadConfig();
	} catch (e: any) {
		error.value = errorMessage(e, "Failed to load the VectorSurv integration.");
		return;
	}
	await Promise.all([loadRuns(), loadReadiness(), loadRecords(), loadMirror()]);
}

// filterState narrows the ledger to one state and reason, which is how the
// blocked tally opens the records behind it.
async function filterState(state: string, _reason: string): Promise<void> {
	filters.state = state;
	await loadRecords();
}

async function openRecord(id: number): Promise<void> {
	detail.value = await apiClient.JSONGet<VectorSurvSyncObject>(
		`/api/integration/vectorsurv/records/${id}`,
	);
	events.value = await apiClient.JSONGet<VectorSurvSyncEvent[]>(
		`/api/integration/vectorsurv/records/${id}/events`,
	);
}

function closeRecord(): void {
	detail.value = null;
	events.value = [];
}

// pretty renders one stored body for display. A body that is not JSON (an empty
// request, a bare string) is shown as it arrived.
function pretty(body: unknown): string {
	if (body === null || body === undefined) return "—";
	if (typeof body === "string") {
		try {
			return JSON.stringify(JSON.parse(body), null, 2);
		} catch {
			return body;
		}
	}
	return JSON.stringify(body, null, 2);
}

// previousRequest returns the older attempt's body for a diff, or null for the
// oldest attempt: the first filing has nothing to be compared against.
function previousRequest(index: number): unknown {
	const older = events.value[index + 1];
	return older ? older.request : null;
}

// requestDiff compares an attempt's request body with the previous one, line by
// line. Two bodies of different line counts are compared over the shorter one
// and the rest is marked changed, which is enough to show what a re-filing
// altered without shipping a diff engine.
function requestDiff(index: number): { text: string; changed: boolean }[] {
	const current = pretty(events.value[index]?.request).split("\n");
	const previous = pretty(events.value[index + 1]?.request).split("\n");
	const lines: { text: string; changed: boolean }[] = [];
	const most = Math.max(current.length, previous.length);
	for (let i = 0; i < most; i += 1) {
		const now = current[i] ?? "";
		const then = previous[i] ?? "";
		lines.push({ text: now, changed: now !== then });
	}
	return lines;
}

async function startFiling(): Promise<void> {
	starting.value = true;
	actionError.value = "";
	actionResult.value = "";
	try {
		config.value = await apiClient.JSONPost<VectorSurvConfig>(
			"/api/integration/vectorsurv/filing/start",
			{},
		);
		actionResult.value = "Filing started; the first pass is queued.";
		await Promise.all([loadRuns(), loadReadiness()]);
	} catch (e: any) {
		actionError.value = errorMessage(e, "Failed to start filing.");
	} finally {
		starting.value = false;
	}
}

async function pauseFiling(): Promise<void> {
	pausing.value = true;
	actionError.value = "";
	actionResult.value = "";
	try {
		config.value = await apiClient.JSONPost<VectorSurvConfig>(
			"/api/integration/vectorsurv/filing/pause",
			{},
		);
		actionResult.value = "Filing paused; the cutover is unchanged.";
	} catch (e: any) {
		actionError.value = errorMessage(e, "Failed to pause filing.");
	} finally {
		pausing.value = false;
	}
}

async function runNow(): Promise<void> {
	filingNow.value = true;
	actionError.value = "";
	actionResult.value = "";
	try {
		const result = await apiClient.JSONPost<VectorSurvSyncQueued>(
			"/api/integration/vectorsurv/file",
			{},
		);
		actionResult.value = result.queued
			? "A filing pass is queued."
			: "A filing pass is already queued, or filing is not enabled.";
		await loadRuns();
	} catch (e: any) {
		actionError.value = errorMessage(e, "Failed to queue a filing pass.");
	} finally {
		filingNow.value = false;
	}
}

async function fullMirror(): Promise<void> {
	mirroring.value = true;
	actionError.value = "";
	actionResult.value = "";
	try {
		const result = await apiClient.JSONPost<VectorSurvSyncQueued>(
			"/api/integration/vectorsurv/sync/full",
			{},
		);
		actionResult.value = result.queued
			? "Full mirror queued; it runs as a background job."
			: "A full mirror is already queued or running.";
		await loadMirror();
	} catch (e: any) {
		actionError.value = errorMessage(e, "Failed to start the full mirror.");
	} finally {
		mirroring.value = false;
	}
}

onMounted(load);
</script>

<style scoped>
.sync-card {
	border-left: 5px solid #0d6efd;
}
.clickable-row {
	cursor: pointer;
}
.ledger-table,
.mirror-counts {
	max-height: 28rem;
	overflow-y: auto;
}
.payload {
	background: #f8f9fa;
	border: 1px solid #dee2e6;
	border-radius: 0.25rem;
	font-size: 0.75rem;
	margin-bottom: 0;
	max-height: 16rem;
	overflow: auto;
	padding: 0.5rem;
	white-space: pre-wrap;
	word-break: break-all;
}
.diff-changed {
	background: #fff3cd;
	display: inline-block;
	width: 100%;
}
.blocked-group {
	cursor: pointer;
}
</style>
